import React, { useState, useEffect, useCallback } from 'react';
import './CreacionPedido.css';
import './BuscadorProductos.css'; // Importamos los nuevos estilos
import DetallePedidoMejorado from './DetallePedidoMejorado';
import ProductoCardMejorado from './ProductoCard';
import ProductoCardConIngrediente from './ProductoCardConIngrediente';
import API from '../../../utils/api';
import { PromocionesProvider } from '../../../contexts/PromocionesContext';


/**
 * Componente actualizado para la creación de pedidos
 * Con soporte mejorado para promociones tipo 3
 */
const CreacionPedidoActualizado = ({
  // Datos
  categorias = [],
  productos = [],
  pedidoActual = [],
  productosNuevos = [],
  productosGuardados = [],
  mesaActual = null, // Información de la mesa actual
  promociones = [],
  promocionesAplicadas = [],
  calculandoPromociones = false,
  categoriaSeleccionada,
  loading = false,
  deshabilitarAgregarProductos = false,

  // Props para promociones de mesa en pedidos para llevar
  tipoPedido = 'mesa',
  promocionMesaActiva = false,
  onTogglePromocionMesa = null,

  // Callbacks
  onAgregarProducto,
  onCambiarCantidad,
  onEliminarProducto,
  onEliminarProductoGuardado,
  onLimpiarPedido,
  onImprimirComanda,
  onCambiarCategoria,
  onEditarInstrucciones,
  onRecargarPedido
}) => {
  // Estado para controlar si estamos viendo categorías o productos
  const [vistaActual, setVistaActual] = useState('categorias');

  // Estado para modal de detalles del producto
  const [productoDetalle, setProductoDetalle] = useState(null);

  // Estado para el buscador
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [buscando, setBuscando] = useState(false);

  // Opciones de búsqueda
  const [opcionesBusqueda, setOpcionesBusqueda] = useState({
    porNombre: true,
    porIngrediente: false,
    sinIngrediente: false
  });

  // Filtrar productos según categoría seleccionada o búsqueda
  const productosFiltrados = useCallback(() => {
    if (!productos || productos.length === 0) return [];

    // Si estamos buscando, mostrar resultados de búsqueda
    if (terminoBusqueda && resultadosBusqueda.length > 0) {
      return resultadosBusqueda;
    }

    // En modo de vista de productos, devolver todos los productos
    // de la categoría actual
    if (vistaActual === 'productos') {
      return productos;
    }

    return [];
  }, [productos, vistaActual, terminoBusqueda, resultadosBusqueda]);

  // Función para buscar productos
  const buscarProductos = useCallback(async (termino) => {
    if (!termino.trim()) {
      setResultadosBusqueda([]);
      setBuscando(false);
      return;
    }

    setBuscando(true);

    try {
      let resultados = [];

      // Si busca por nombre de platillo
      if (opcionesBusqueda.porNombre) {
        // Llamar al endpoint de búsqueda por nombre
        const resultadosNombre = await API.productos.buscar(termino);
        resultados = [...resultadosNombre];
      }

      // Si busca por ingrediente
      if (opcionesBusqueda.porIngrediente || opcionesBusqueda.sinIngrediente) {
        // Llamar al endpoint de búsqueda por ingredientes
        const resultadosIngredientes = await API.productos.buscarPorIngrediente(
          termino,
          opcionesBusqueda.sinIngrediente // true busca platillos sin el ingrediente, false con el ingrediente
        );

        // Combinar resultados evitando duplicados
        resultadosIngredientes.forEach(producto => {
          if (!resultados.some(p => p.idPlatillo === producto.idPlatillo || p.idProducto === producto.idProducto)) {
            resultados.push(producto);
          }
        });
      }

      setResultadosBusqueda(resultados || []);
    } catch (error) {
      console.error("Error al buscar productos:", error);

      // Búsqueda local como fallback
      let resultadosLocales = [];

      if (opcionesBusqueda.porNombre) {
        const porNombre = productos.filter(producto =>
          producto.nombre.toLowerCase().includes(termino.toLowerCase())
        );
        resultadosLocales = [...porNombre];
      }

      // Nota: La búsqueda local por ingredientes es limitada sin datos de ingredientes
      // Aquí podríamos implementar un algoritmo básico si tuviéramos los datos de ingredientes localmente

      setResultadosBusqueda(resultadosLocales);
    } finally {
      setBuscando(false);
    }
  }, [productos, opcionesBusqueda]);

  // Efecto para manejar la búsqueda con un pequeño debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (terminoBusqueda) {
        buscarProductos(terminoBusqueda);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [terminoBusqueda, buscarProductos]);

  // Mostrar detalles de un producto (ingredientes)
  const mostrarDetallesProducto = async (e, producto) => {
    e.stopPropagation(); // Evitar que se agregue al pedido al mostrar detalles

    setProductoDetalle({
      ...producto,
      cargandoIngredientes: true,
      ingredientes: [],
      errorIngredientes: null
    });

    try {
      // Cargar ingredientes bajo demanda
      const respuesta = await API.productos.getIngredientesProducto(
        producto.idProducto || producto.idPlatillo
      );

      setProductoDetalle(prevState => ({
        ...prevState,
        ingredientes: respuesta || [],
        cargandoIngredientes: false
      }));
    } catch (error) {
      console.error("Error al cargar ingredientes:", error);
      setProductoDetalle(prevState => ({
        ...prevState,
        cargandoIngredientes: false,
        errorIngredientes: "No se pudieron cargar los ingredientes"
      }));
    }
  };

  // Cerrar modal de detalles
  const cerrarDetallesProducto = () => {
    setProductoDetalle(null);
  };

  // Función para seleccionar una categoría
  const seleccionarCategoria = (categoria) => {
    onCambiarCategoria(categoria);
    setVistaActual('productos');
  };

  // Volver a la vista de categorías
  const volverACategorias = () => {
    setVistaActual('categorias');
    // Limpiar búsqueda al volver a categorías
    setTerminoBusqueda('');
    setResultadosBusqueda([]);
  };

  // Limpiar la búsqueda
  const limpiarBusqueda = () => {
    setTerminoBusqueda('');
    setResultadosBusqueda([]);
    if (vistaActual === 'busqueda') {
      setVistaActual('categorias');
    }
  };

  // Cambiar opción de búsqueda
  const cambiarOpcionBusqueda = (opcion) => {
    setOpcionesBusqueda(prev => {
      const nuevasOpciones = { ...prev, [opcion]: !prev[opcion] };

      // Si se desactivan todas las opciones, activar búsqueda por nombre
      if (!nuevasOpciones.porNombre && !nuevasOpciones.porIngrediente && !nuevasOpciones.sinIngrediente) {
        nuevasOpciones.porNombre = true;
      }

      // Si se activa "sin ingrediente", asegurar que "por ingrediente" está desactivado
      if (opcion === 'sinIngrediente' && nuevasOpciones.sinIngrediente) {
        nuevasOpciones.porIngrediente = false;
      }

      // Si se activa "por ingrediente", asegurar que "sin ingrediente" está desactivado
      if (opcion === 'porIngrediente' && nuevasOpciones.porIngrediente) {
        nuevasOpciones.sinIngrediente = false;
      }

      return nuevasOpciones;
    });

    // Si hay un término de búsqueda activo, ejecutar la búsqueda con las nuevas opciones
    if (terminoBusqueda.trim()) {
      buscarProductos(terminoBusqueda);
    }
  };

  // Renderizado condicional: categorías, productos o resultados de búsqueda
  const renderizarContenidoPrincipal = () => {
    // Renderizar el buscador en todas las vistas
    const buscador = (
      <div className={`buscador-container ${deshabilitarAgregarProductos ? 'deshabilitado' : ''}`}>
        <div className="buscador-input-container">
          <span className="buscador-icon">🔍</span>
          <input
            type="text"
            className={`buscador-input ${deshabilitarAgregarProductos ? 'deshabilitado' : ''}`}
            placeholder={deshabilitarAgregarProductos ? "Búsqueda deshabilitada - Pedido pendiente" : "Buscar productos..."}
            value={terminoBusqueda}
            onChange={deshabilitarAgregarProductos ? null : (e) => setTerminoBusqueda(e.target.value)}
            disabled={deshabilitarAgregarProductos}
          />
          {terminoBusqueda && !deshabilitarAgregarProductos && (
            <button className="buscador-clear" onClick={limpiarBusqueda}>
              ✕
            </button>
          )}
        </div>

        <div className={`buscador-opciones ${deshabilitarAgregarProductos ? 'deshabilitado' : ''}`}>
          <label
            className={`opcion-checkbox ${deshabilitarAgregarProductos ? 'disabled' : ''}`}
            onClick={deshabilitarAgregarProductos ? null : () => cambiarOpcionBusqueda('porNombre')}
          >
            <span className={`checkbox-custom ${opcionesBusqueda.porNombre ? 'checked' : ''}`}>
              {opcionesBusqueda.porNombre && <span className="checkmark">✓</span>}
            </span>
            <span className="checkbox-label">Por nombre</span>
          </label>

          <label
            className={`opcion-checkbox ${deshabilitarAgregarProductos ? 'disabled' : ''}`}
            onClick={deshabilitarAgregarProductos ? null : () => cambiarOpcionBusqueda('porIngrediente')}
          >
            <span className={`checkbox-custom ${opcionesBusqueda.porIngrediente ? 'checked' : ''}`}>
              {opcionesBusqueda.porIngrediente && <span className="checkmark">✓</span>}
            </span>
            <span className="checkbox-label">Con ingrediente</span>
          </label>

          <label
            className={`opcion-checkbox ${deshabilitarAgregarProductos ? 'disabled' : ''}`}
            onClick={deshabilitarAgregarProductos ? null : () => cambiarOpcionBusqueda('sinIngrediente')}
          >
            <span className={`checkbox-custom ${opcionesBusqueda.sinIngrediente ? 'checked' : ''}`}>
              {opcionesBusqueda.sinIngrediente && <span className="checkmark">✓</span>}
            </span>
            <span className="checkbox-label">Sin ingrediente</span>
          </label>
        </div>
      </div>
    );

    // Si hay un término de búsqueda, mostrar resultados
    if (terminoBusqueda) {
      return (
        <>
          {buscador}
          <div className="productos-header busqueda-header">
            <h3>Resultados de búsqueda: "{terminoBusqueda}"</h3>
            <button className="btn-volver" onClick={limpiarBusqueda}>
              ← Volver
            </button>
          </div>

          <div className="productos-grid">
            {buscando ? (
              <div className="cargando-productos">
                <div className="spinner"></div>
                <p>Buscando productos...</p>
              </div>
            ) : (
              <>
                {resultadosBusqueda.length > 0 ? (
                  resultadosBusqueda.map(producto => {
                    // Determinar si el producto se encontró por ingrediente
                    const esResultadoPorIngrediente = producto.ingrediente_encontrado ||
                      (opcionesBusqueda.porIngrediente && producto._matchPorIngrediente);

                    // Determinar si es un resultado de exclusión de ingrediente
                    const esResultadoExcluido = opcionesBusqueda.sinIngrediente &&
                      producto._matchSinIngrediente;

                    // Usar el componente adecuado según el tipo de resultado
                    if (esResultadoPorIngrediente || esResultadoExcluido) {
                      return (
                        <ProductoCardConIngrediente
                          key={producto.idProducto || producto.idPlatillo}
                          producto={producto}
                          onAgregarProducto={onAgregarProducto}
                          onVerDetalles={mostrarDetallesProducto}
                          ingredienteEncontrado={producto.ingrediente_encontrado || ''}
                          esExcluido={esResultadoExcluido}
                        />
                      );
                    } else {
                      return (
                        <ProductoCardMejorado
                          key={producto.idProducto || producto.idPlatillo}
                          producto={producto}
                          onAgregarProducto={onAgregarProducto}
                          onVerDetalles={mostrarDetallesProducto}
                        />
                      );
                    }
                  })
                ) : (
                  <div className="no-productos">
                    No se encontraron productos para "{terminoBusqueda}"
                  </div>
                )}
              </>
            )}
          </div>
        </>
      );
    }
    // Vista de categorías
    else if (vistaActual === 'categorias') {
      return (
        <>
          {buscador}
          {deshabilitarAgregarProductos && (
            <div className="pedido-pendiente-alerta">
              <div className="alerta-icono">⚠️</div>
              <div className="alerta-contenido">
                <h3>Pedido en Estado Pendiente</h3>
                <p>No se pueden agregar productos a un pedido pendiente. Debe abrir la cuenta primero.</p>
              </div>
            </div>
          )}
          <div className={`categorias-grid ${deshabilitarAgregarProductos ? 'deshabilitado' : ''}`}>
            {categorias.map(categoria => (
              <button
                key={categoria.idCategoria}
                className={`categoria-card ${deshabilitarAgregarProductos ? 'deshabilitada' : ''}`}
                onClick={deshabilitarAgregarProductos ? null : () => seleccionarCategoria(categoria)}
                disabled={deshabilitarAgregarProductos}
                style={{
                  backgroundColor: deshabilitarAgregarProductos ? '#e0e0e0' : (categoria.colorFondo || '#f5f5f5'),
                  color: deshabilitarAgregarProductos ? '#999' : (categoria.colorLetra || '#333'),
                  cursor: deshabilitarAgregarProductos ? 'not-allowed' : 'pointer'
                }}
              >
                {categoria.nombre}
              </button>
            ))}
          </div>
        </>
      );
    }
    // Vista de productos
    else {
      return (
        <>
          {buscador}
          <div className="productos-header">
            <h3>{categoriaSeleccionada?.nombre || 'Productos'}</h3>
            <button className="btn-volver" onClick={volverACategorias}>
              ← Volver a Categorías
            </button>
          </div>

          <div className="productos-grid">
            {loading ? (
              <div className="cargando-productos">
                <div className="spinner"></div>
                <p>Cargando productos...</p>
              </div>
            ) : (
              <>
                {productosFiltrados().map(producto => (
                  <ProductoCardMejorado
                    key={producto.idProducto || producto.idPlatillo}
                    producto={producto}
                    onAgregarProducto={onAgregarProducto}
                    onVerDetalles={mostrarDetallesProducto}
                  />
                ))}

                {productosFiltrados().length === 0 && (
                  <div className="no-productos">
                    No hay productos en esta categoría
                  </div>
                )}
              </>
            )}
          </div>
        </>
      );
    }
  };

  return (
    <PromocionesProvider
      pedidoActual={pedidoActual}
      promocionesDisponibles={promociones}
    >
      <div className="creacion-pedido-content">
        <div className="productos-seccion">
          {renderizarContenidoPrincipal()}
        </div>

        {/* Modal de detalles del producto */}
        {productoDetalle && (
          <div className="modal-overlay" onClick={cerrarDetallesProducto}>
            <div className="modal-producto-detalle" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{productoDetalle.nombre}</h3>
                <button className="btn-cerrar-modal" onClick={cerrarDetallesProducto}>×</button>
              </div>

              <div className="modal-content">
                <div className="producto-precio-detalle">
                  <span>Precio:</span> ${parseFloat(productoDetalle.precio).toFixed(2)}
                </div>

                <div className="producto-ingredientes">
                  <h4>Ingredientes:</h4>

                  {productoDetalle.cargandoIngredientes ? (
                    <div className="cargando-ingredientes">
                      <div className="spinner-small"></div>
                      <p>Cargando ingredientes...</p>
                    </div>
                  ) : productoDetalle.errorIngredientes ? (
                    <p className="error-ingredientes">{productoDetalle.errorIngredientes}</p>
                  ) : productoDetalle.ingredientes && productoDetalle.ingredientes.length > 0 ? (
                    <ul className="lista-ingredientes">
                      {productoDetalle.ingredientes.map((ingrediente, index) => (
                        <li key={index}>
                          {ingrediente.nombre || ingrediente}
                          {ingrediente.cantidad && <span className="cantidad-ingrediente">({ingrediente.cantidad})</span>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-ingredientes">Este producto no tiene ingredientes registrados.</p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-agregar-desde-detalle"
                  onClick={() => {
                    onAgregarProducto(productoDetalle);
                    cerrarDetallesProducto();
                  }}
                >
                  Agregar al pedido
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Usar el componente de detalle de pedido */}
        <DetallePedidoMejorado
          pedidoActual={pedidoActual}
          productosNuevos={productosNuevos}
          productosGuardados={productosGuardados}
          mesaActual={mesaActual}
          onCambiarCantidad={onCambiarCantidad}
          onEliminarProducto={onEliminarProducto}
          onEliminarProductoGuardado={onEliminarProductoGuardado}
          onLimpiarPedido={onLimpiarPedido}
          onImprimirComanda={onImprimirComanda}
          onEditarInstrucciones={onEditarInstrucciones}
          onRecargarPedido={onRecargarPedido}
          promocionesAplicadas={promocionesAplicadas}
          calculandoPromociones={calculandoPromociones}
          tipoPedido={tipoPedido}
          promocionMesaActiva={promocionMesaActiva}
          onTogglePromocionMesa={onTogglePromocionMesa}
        />
      </div>
    </PromocionesProvider>
  );
};

export default CreacionPedidoActualizado